import type { LatLng } from '@fdt/shared';
import ClearIcon from '@mui/icons-material/Clear';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, errorKey } from '../api/client';

/**
 * Leaflet resolves its default marker images relative to the CSS, which a
 * bundler rewrites — so the icon is rebuilt from the imported asset URLs.
 */
const DEFAULT_ICON = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

interface GeocodeResult {
  label: string;
  location: LatLng;
  address?: { street?: string; comune?: string; provincia?: string; cap?: string };
}

interface MapPickerProps {
  value: LatLng | null;
  onChange: (location: LatLng | null) => void;
  /** Called when a searched address is chosen, to prefill the address fields. */
  onAddressPicked?: (address: NonNullable<GeocodeResult['address']>) => void;
  /** Tile URL from settings, so the tile source stays configurable. */
  tileUrl: string;
  height?: number;
}

/** Milan, as a starting view when no customer has been placed yet. */
const DEFAULT_CENTER: LatLng = { lat: 45.4642, lng: 9.19 };

export function MapPicker({
  value,
  onChange,
  onAddressPicked,
  tileUrl,
  height = 320,
}: MapPickerProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [term, setTerm] = useState('');
  const [submittedTerm, setSubmittedTerm] = useState('');

  const search = useQuery<GeocodeResult[]>({
    queryKey: ['geocode', submittedTerm],
    queryFn: async () => (await api.get('/geocode', { params: { q: submittedTerm } })).data,
    enabled: submittedTerm.length >= 3,
    // The provider is rate-limited; results for one string do not change.
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  // Create the map once. Leaflet is imperative, so it lives outside React state.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) {
      return;
    }

    const map = L.map(containerRef.current).setView(
      [value?.lat ?? DEFAULT_CENTER.lat, value?.lng ?? DEFAULT_CENTER.lng],
      value ? 16 : 11,
    );

    L.tileLayer(tileUrl, {
      maxZoom: 19,
      // OpenStreetMap's licence requires visible attribution.
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    map.on('click', (event: L.LeafletMouseEvent) => {
      onChangeRef.current({ lat: event.latlng.lat, lng: event.latlng.lng });
    });

    mapRef.current = map;

    // The dialog animates open, so the map is sized after the first paint.
    const timer = setTimeout(() => map.invalidateSize(), 250);

    return () => {
      clearTimeout(timer);
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // Intentionally one-shot: the tile URL and initial value are read once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the marker in step with the value, wherever the change came from.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }

    if (!value) {
      markerRef.current?.remove();
      markerRef.current = null;
      return;
    }

    if (markerRef.current) {
      markerRef.current.setLatLng([value.lat, value.lng]);
    } else {
      const marker = L.marker([value.lat, value.lng], {
        icon: DEFAULT_ICON,
        draggable: true,
      }).addTo(map);

      marker.on('dragend', () => {
        const position = marker.getLatLng();
        onChangeRef.current({ lat: position.lat, lng: position.lng });
      });

      markerRef.current = marker;
    }
  }, [value]);

  function pick(result: GeocodeResult): void {
    onChange(result.location);
    onAddressPicked?.(result.address ?? {});
    mapRef.current?.setView([result.location.lat, result.location.lng], 17);
    setSubmittedTerm('');
    setTerm(result.label);
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1}>
        <TextField
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              // Inside a form dialog, Enter would otherwise submit the whole form.
              event.preventDefault();
              setSubmittedTerm(term.trim());
            }
          }}
          placeholder={t('geocoding.search')}
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search.isFetching ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
        <Button variant="outlined" onClick={() => setSubmittedTerm(term.trim())}>
          {t('common.search')}
        </Button>
      </Stack>

      {search.isError && <Alert severity="warning">{t(errorKey(search.error))}</Alert>}

      {search.data && search.data.length === 0 && submittedTerm.length >= 3 && (
        <Alert severity="info">{t('geocoding.noResults')}</Alert>
      )}

      {search.data && search.data.length > 0 && (
        <Paper variant="outlined" sx={{ maxHeight: 160, overflow: 'auto' }}>
          <List dense disablePadding>
            {search.data.map((result) => (
              <ListItemButton key={result.label} onClick={() => pick(result)}>
                <ListItemText primary={result.label} />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}

      <Box
        ref={containerRef}
        sx={{
          height,
          borderRadius: 1,
          overflow: 'hidden',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          // Leaflet's own panes must not sit above MUI's dialog layer.
          '& .leaflet-pane': { zIndex: 1 },
          '& .leaflet-control': { zIndex: 2 },
        }}
      />

      <Stack direction="row" spacing={1} alignItems="center">
        {value ? (
          <>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {value.lat.toFixed(6)}, {value.lng.toFixed(6)}
            </Typography>
            <IconButton
              size="small"
              onClick={() => mapRef.current?.setView([value.lat, value.lng], 17)}
              aria-label={t('customers.centreOnPin')}
            >
              <MyLocationIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => onChange(null)}
              aria-label={t('customers.clearLocation')}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('geocoding.hint')}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}
